/* eslint-disable @typescript-eslint/no-explicit-any */
import { afterNextRender, Component, Inject, signal } from '@angular/core';
import { NonNullableFormBuilder, Validators } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialog, MatDialogRef } from '@angular/material/dialog';
import {
    catchError,
    debounceTime,
    distinctUntilChanged,
    EMPTY,
    finalize,
    Observable,
    Subject,
    switchMap,
    tap,
} from 'rxjs';
import { MAX_FILE_SIZE_LIMIT } from '../../../../constants/general.constant';
import { GroupRoleTypeEnum } from '../../../../constants/group-role.constant';
import { getPrivacyTypeLabel, PrivacyTypeEnum } from '../../../../constants/privacy.constant';
import { IGroupCategoryModel, IGroupModel, IUpdateGroupDetailModel } from '../../../../models/groups.model';
import { IPostListingFilterModel } from '../../../../models/post.model';
import { IUserListingFilterModel, IUserModel } from '../../../../models/user.model';
import { GroupsService } from '../../../../services/api/groups/groups.service';
import { UploadService } from '../../../../services/api/upload/upload.service';
import { UserService } from '../../../../services/api/user/user.service';
import { GroupContextService } from '../../../../services/helper/group-context/group-context.service';
import { LoaderService } from '../../../../services/loader/loader.service';
import { BasePopupComponent } from '../base-popup/base-popup.component';
import { SuccessPopupComponent } from '../success-popup/success-popup.component';

@Component({
    selector: 'app-group-manage-popup',
    standalone: false,
    templateUrl: './group-manage-popup.component.html',
    styleUrl: './group-manage-popup.component.scss',
})
export class GroupManagePopupComponent extends BasePopupComponent {
    protected selectedTab: string = 'Details';
    protected groupData = signal<IGroupModel>({} as IGroupModel);
    // Group Details
    protected editForm = this.fb.group({
        name: ['', [Validators.required]],
        description: ['', [Validators.required]],
        photo: [null as number | null],
        cats: [[] as number[]],
    });
    protected coverImage = signal<File | null>(null);
    protected coverImageUrl = signal<string | null>(null);
    protected coverImageId = signal<number | null>(null);
    protected groupCategories = signal<IGroupCategoryModel[]>([]);
    protected selectedGroupCategories = signal<IGroupCategoryModel[]>([]);
    // Manage Group
    protected totalMemberJoinRequests = signal<number | null>(null);
    protected memberJoinRequestsData = signal<IUserModel[]>([]);
    protected totalMembers = signal<number | null>(null);
    protected membersData = signal<IUserModel[]>([]);
    protected isJoinRequestsLoading = signal<boolean>(true);
    protected isInitialLoading = signal<boolean>(true); // For skeleton loader
    protected GroupRoleTypeEnum = GroupRoleTypeEnum;
    // Invite Members
    protected usersData = signal<IUserModel[]>([]);
    protected selectedUsers = signal<number[]>([]);
    protected PrivacyTypeEnum = PrivacyTypeEnum;
    protected getPrivacyTypeLabel = getPrivacyTypeLabel;

    private groupCategorySearchSubject = new Subject<string>();
    private page = 1;
    private size = 20;
    private allLoaded = false;
    private userSearchSubject = new Subject<string>();
    private currentKeyword = '';

    constructor(
        @Inject(MAT_DIALOG_DATA) protected override data: any,
        protected override dialogRef: MatDialogRef<GroupManagePopupComponent>,
        private dialog: MatDialog,
        private fb: NonNullableFormBuilder,
        private groupsService: GroupsService,
        private groupContextService: GroupContextService,
        private loaderService: LoaderService,
        private uploadService: UploadService,
        private userService: UserService,
    ) {
        super(data, dialogRef);
        afterNextRender(() => {
            this.groupContextService.memberJoinRequestList$.subscribe(() => {
                this.loadGroupMemberJoinRequests(); // reload member join requests when a member is approved/rejected
            });
            this.groupContextService.memberList$.subscribe(() => {
                this.loadMembers(true).subscribe(); // reload members when a member is added/removed
            });
        });
    }

    override ngOnInit(): void {
        super.ngOnInit();
        this.getGroupDetails();
        this.loadGroupCategories();
        this.setupCategorySearchListener();
        this.setupUserSearchListener();
    }

    protected onTabSelect(tab: string): void {
        if (this.selectedTab !== tab) {
            this.coverImage.set(null);
            this.coverImageUrl.set(null);
            this.coverImageId.set(null);
            this.selectedGroupCategories.set([]);
            this.totalMemberJoinRequests.set(null);
            this.memberJoinRequestsData.set([]);
            this.totalMembers.set(null);
            this.membersData.set([]);
            this.usersData.set([]);
            this.selectedUsers.set([]);
            this.allLoaded = false;
            this.currentKeyword = '';
            this.selectedTab = tab;
        }
        if (this.selectedTab === 'Details') {
            this.getGroupDetails();
            this.loadGroupCategories();
            this.setupCategorySearchListener();
        }
        if (this.selectedTab === 'Manage') {
            this.loadGroupMemberJoinRequests();
            this.loadMembers(true).subscribe();
        }
        if (this.selectedTab === 'Invite') {
            this.getGroupDetails();
            this.loadUsers().subscribe();
        }
    }

    // Group Details
    protected onFileSelected(event: Event): void {
        const input = event.target as HTMLInputElement;
        const file = input?.files?.[0];
        if (!file) {
            return;
        }
        if (file.size > MAX_FILE_SIZE_LIMIT) {
            const errorPopupConfig = SuccessPopupComponent.getDialogConfig();
            errorPopupConfig.data = {
                title: `Error`,
                message: `File size must not exceed 10 MB.`,
            };
            this.dialog.open(SuccessPopupComponent, errorPopupConfig);
            input.value = '';
            return;
        }
        this.coverImage.set(file);
        this.coverImageUrl.set(URL.createObjectURL(file));
    }

    protected uploadCoverImage(): void {
        const formData = new FormData();
        formData.append('file', this.coverImage() as Blob);

        this.loaderService.show();
        this.uploadService.uploadPhoto(formData).subscribe({
            next: (res) => {
                this.coverImageId.set(res.id);
                this.editForm.controls.photo.setValue(res.id);
                this.updateGroup();
            },
            error: (error) => {
                const errorPopupConfig = SuccessPopupComponent.getDialogConfig();
                errorPopupConfig.data = {
                    title: `Error`,
                    message: error.error?.message,
                };
                this.dialog.open(SuccessPopupComponent, errorPopupConfig);
                console.error('Error on upload cover image', error);
            },
            complete: () => {
                this.loaderService.hide();
            },
        });
    }

    protected updateGroup(): void {
        const body = this.editForm.value as IUpdateGroupDetailModel;
        this.loaderService.show();
        this.groupsService.updateGroupDetail(this.data.groupId, body).subscribe({
            next: () => {
                const popupConfig = SuccessPopupComponent.getDialogConfig();
                popupConfig.data = {
                    title: 'Group Refreshed',
                    message: 'You have successfully updated the group details.',
                    iconPath: '/assets/images/shared/success-green-icon.svg',
                    confirmButtonLabel: 'Great',
                };
                const successDialogRef = this.dialog.open(SuccessPopupComponent, popupConfig);
                successDialogRef.afterClosed().subscribe(() => {
                    this.dialogRef.close();
                });
            },
            error: (error) => {
                const errorPopupConfig = SuccessPopupComponent.getDialogConfig();
                errorPopupConfig.data = {
                    title: `Error`,
                    message: error.error?.message,
                };
                this.dialog.open(SuccessPopupComponent, errorPopupConfig);
                console.error('Error updating group', error);
            },
            complete: () => {
                this.loaderService.hide();
            },
        });
    }

    protected patchEditForm(groupData: IGroupModel): void {
        this.editForm.patchValue({
            name: groupData.name,
            description: groupData.description,
            cats: groupData.cats ? groupData.cats.map((cat) => cat.id) : [],
        });
        this.selectedGroupCategories.set(groupData.cats ?? []);
        this.coverImageUrl.set(groupData.photo);
    }

    protected submit(): void {
        if (!this.editForm.valid) {
            return;
        }

        if (this.coverImage()) {
            this.uploadCoverImage();
        } else {
            this.updateGroup();
        }
    }

    protected loadGroupCategories(keyword: string = ''): void {
        this.loaderService.show();
        this.groupsService.getGroupCategoryListing(999, keyword).subscribe({
            next: (response) => {
                const selected = this.selectedGroupCategories();
                const merged = [...response];
                for (const sel of selected) {
                    if (!merged.some((cat) => cat.id === sel.id)) {
                        merged.unshift(sel); // Add selected to front if not in response
                    }
                }
                this.groupCategories.set(merged);
            },
            error: (error) => {
                const errorPopupConfig = SuccessPopupComponent.getDialogConfig();
                errorPopupConfig.data = {
                    title: `Error`,
                    message: error.error?.message,
                };
                this.dialog.open(SuccessPopupComponent, errorPopupConfig);
                console.error('Error getting group categories', error);
            },
            complete: () => {
                this.loaderService.hide();
            },
        });
    }

    protected toggleCategory(category: IGroupCategoryModel): void {
        const current = [...this.selectedGroupCategories()];
        const index = current.findIndex((c) => c.id === category.id);

        if (index > -1) {
            current.splice(index, 1);
        } else {
            current.push(category);
        }

        this.selectedGroupCategories.set(current ?? []);
        this.editForm.controls.cats.setValue(current.map((item) => item.id));
    }

    protected isCategorySelected(category: IGroupCategoryModel): boolean {
        return this.selectedGroupCategories().some((c) => c.id === category.id);
    }

    // Called on (input)
    protected onCategorySearchInput(query: string): void {
        this.groupCategorySearchSubject.next(query);
    }

    // Manage Group
    protected loadMembers(
        reset = false,
        filter?: IPostListingFilterModel,
    ): Observable<{ total: number; result: IUserModel[] }> {
        if (this.allLoaded && !reset) {
            return EMPTY;
        }

        if (reset) {
            this.page = 1;
            this.allLoaded = false;
            this.membersData.set([]);
            this.isInitialLoading.set(true);
        }

        this.loaderService.show();
        return this.groupsService
            .getGroupMembersListing(this.data.groupId, { ...filter, page: this.page, size: this.size })
            .pipe(
                tap((response) => {
                    this.totalMembers.set(response.total);
                    this.membersData.update((prev) => [...prev, ...response.result]);
                    if (response.result.length < this.size) {
                        this.allLoaded = true;
                    } else {
                        this.page++;
                    }
                }),
                catchError((error) => {
                    const errorPopupConfig = SuccessPopupComponent.getDialogConfig();
                    errorPopupConfig.data = {
                        title: `Error`,
                        message: error.error?.message,
                    };
                    this.dialog.open(SuccessPopupComponent, errorPopupConfig);
                    console.error('Error getting group members listing:', error);
                    return EMPTY;
                }),
                finalize(() => {
                    this.isInitialLoading.set(false);
                    this.loaderService.hide();
                }),
            );
    }

    protected onScrollManageGroup(): void {
        this.loadMembers(false).subscribe();
    }

    protected onScrollInviteMember(): void {
        this.loadUsers(false, { keyword: this.currentKeyword }).subscribe();
    }

    // Invite Members
    // Called on (input)
    protected onUserSearchInput(query: string): void {
        this.userSearchSubject.next(query);
    }

    protected loadUsers(reset = false, filter?: IUserListingFilterModel): Observable<IUserModel[]> {
        if (this.allLoaded && !reset) {
            return EMPTY;
        }

        if (reset) {
            this.page = 1;
            this.allLoaded = false;
            this.usersData.set([]);
            this.isInitialLoading.set(true);
        }

        this.loaderService.show();
        return this.userService
            .getUsersListing({ ...filter, page: this.page, size: this.size, notInGroup: this.data.groupId })
            .pipe(
                tap((response) => {
                    this.usersData.update((prev) => [...prev, ...response]);
                    if (response.length < this.size) {
                        this.allLoaded = true;
                    } else {
                        this.page++;
                    }
                }),
                catchError((error) => {
                    const errorPopupConfig = SuccessPopupComponent.getDialogConfig();
                    errorPopupConfig.data = {
                        title: `Error`,
                        message: error.error?.message,
                    };
                    this.dialog.open(SuccessPopupComponent, errorPopupConfig);
                    console.error('Error getting users listing:', error);
                    return EMPTY;
                }),
                finalize(() => {
                    this.isInitialLoading.set(false);
                    this.loaderService.hide();
                }),
            );
    }

    protected toggleUser(userId: number, checked: boolean): void {
        this.selectedUsers.update((prev) => {
            if (checked) {
                return [...prev, userId]; // Add user
            } else {
                return prev.filter((id) => id !== userId); // Remove user
            }
        });
    }

    protected inviteSelectedUsers(): void {
        const users = this.selectedUsers();

        if (users.length === 0) {
            return;
        }

        this.loaderService.show();
        this.groupsService
            .inviteMember(this.data.groupId, users)
            .pipe(
                tap(() => {
                    const popupConfig = SuccessPopupComponent.getDialogConfig();
                    popupConfig.data = {
                        title: `Invitation Sent!`,
                        message: 'Thanks for spreading the word. You have successfully invited user(s) to the group.',
                        iconPath: '/assets/images/shared/success-green-icon.svg',
                        confirmButtonLabel: 'Done',
                    };
                    this.dialog.open(SuccessPopupComponent, popupConfig);
                }),
                catchError((error) => {
                    const errorPopupConfig = SuccessPopupComponent.getDialogConfig();
                    errorPopupConfig.data = {
                        title: `Error`,
                        message: error.error?.message,
                    };
                    this.dialog.open(SuccessPopupComponent, errorPopupConfig);
                    console.error('Error inviting member:', error);
                    return EMPTY;
                }),
                finalize(() => {
                    this.loaderService.hide();
                }),
            )
            .subscribe();
    }

    private setupCategorySearchListener(): void {
        this.groupCategorySearchSubject.pipe(debounceTime(400), distinctUntilChanged()).subscribe((keyword) => {
            this.loadGroupCategories(keyword);
            setTimeout(() => {
                document.getElementById('categorySearchInput')?.focus();
            });
        });
    }

    private getGroupDetails(): void {
        this.loaderService.show();
        this.groupsService.getGroupDetail(this.data.groupId).subscribe({
            next: (res) => {
                this.groupData.set(res);
                this.patchEditForm(res);
            },
            error: (error) => {
                const errorPopupConfig = SuccessPopupComponent.getDialogConfig();
                errorPopupConfig.data = {
                    title: `Error`,
                    message: error.error?.message,
                };
                this.dialog.open(SuccessPopupComponent, errorPopupConfig);
                console.error('Error getting group details', error);
            },
            complete: () => {
                this.loaderService.hide();
            },
        });
    }

    private loadGroupMemberJoinRequests(): void {
        this.loaderService.show();
        this.groupsService
            .getGroupMemberJoinRequestsListing(this.data.groupId)
            .pipe(
                tap((response) => {
                    this.totalMemberJoinRequests.set(response.total);
                    this.memberJoinRequestsData.set(response.result);
                }),
                catchError((error) => {
                    console.error('Error getting group member join requests:', error);
                    return EMPTY;
                }),
                finalize(() => {
                    this.isJoinRequestsLoading.set(false);
                    this.loaderService.hide();
                }),
            )
            .subscribe();
    }

    // Debounced search logic
    private setupUserSearchListener(): void {
        this.userSearchSubject
            .pipe(
                debounceTime(400),
                distinctUntilChanged(),
                tap((keyword) => {
                    this.currentKeyword = keyword;
                }),
                switchMap((keyword) => {
                    this.selectedUsers.set([]);
                    return this.loadUsers(true, { keyword });
                }),
            )
            .subscribe(() => document.getElementById('searchInput')?.focus());
    }
}
